import { BaseApiClient } from '@/services/baseApiClient';
import { type RequestOptions, RequestError, uploadFile } from '@/utils/request';
import { getQuery } from '@/components/filter/helpers';
import { Collection, FloorPlanFormField } from '@enmon/commons/lib/enmon';
import queryString from 'qs';
import type {
  CreateFloorPlanArgs,
  DeleteFloorPlanArgs,
  GetFloorPlansArgs,
  GetOneFloorPlanArgs,
  UpdateFloorPlanArgs,
} from './types/args';
import type {
  DeleteFloorPlanResult,
  GetFloorPlansResult,
  GetOneFloorPlanResult,
  CreateFloorPlanResult,
  UpdateFloorPlanResult,
} from './types/results';
import type { IFloorPlansApiClient } from './types/interface';

export class FloorPlansApiClient extends BaseApiClient implements IFloorPlansApiClient {
  constructor() {
    super('api/floor-plans');
  }

  public async create({ dto, attachments }: CreateFloorPlanArgs): Promise<CreateFloorPlanResult> {
    const formData = new FormData();

    formData.append(FloorPlanFormField.Data, JSON.stringify(dto));

    attachments.forEach(attachment => {
      formData.append(FloorPlanFormField.Image, attachment);
    });

    try {
      return await uploadFile(
        `${this.http.urlPrefix}?${queryString.stringify({
          collection: Collection.FloorPlans,
        })}`,
        formData,
        {
          signal: undefined,
        },
      );
    } catch (error) {
      if (error instanceof RequestError && error.response.status === 412) {
        return await error.response.json<CreateFloorPlanResult>();
      }
      throw error;
    }
  }

  public async get(filter: GetFloorPlansArgs, signal: RequestOptions['signal']): Promise<GetFloorPlansResult> {
    return await this.http.get('', { query: getQuery({ ...filter }), signal });
  }

  public async getOne(id: GetOneFloorPlanArgs, signal: RequestOptions['signal']): Promise<GetOneFloorPlanResult> {
    return await this.http.get(`${id}`, { signal });
  }

  public async updateFloorPlanImage({ id, attachments }: UpdateFloorPlanArgs): Promise<UpdateFloorPlanResult> {
    const formData = new FormData();

    attachments.forEach(attachment => {
      formData.append(FloorPlanFormField.Image, attachment);
    });

    try {
      return await uploadFile(
        `${this.http.urlPrefix}/${id}?${queryString.stringify({
          collection: Collection.FloorPlans,
          id,
        })}`,
        formData,
        {
          method: 'PUT',
          signal: undefined,
        },
      );
    } catch (error) {
      if (error instanceof RequestError && error.response.status === 412) {
        return await error.response.json<CreateFloorPlanResult>();
      }
      throw error;
    }
  }

  public async delete(id: DeleteFloorPlanArgs): Promise<DeleteFloorPlanResult> {
    return await this.http.delete(`${id}`);
  }
}
